import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  selectDisableNextBtnSidePanel,
  selectNewOrderCreation,
  selectedOrderTypeDetails as selectPage1,
  selectPage2,
  selectPage3,
} from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { useTranslation } from 'react-i18next';
import { INewOrderInitialState } from '@AMIEWEB/NewOrder/types';

const useStyles = makeStyles()(theme => ({
  leftTab: {
    position: 'sticky',
    top: '12px',
    height: '90vh',
    width: '188px',
    padding: '0.1rem',
    border: `1px solid ${theme.palette.system.lightFadedGray}`,
    borderRadius: '4px',
    '@media (min-width: 768px) and (max-width: 1024px)': {
      width: '12rem',
    },
  },
  menu: {
    margin: '0.5rem',
  },
  mainMenuItem: {
    height: '19px',
    textAlign: 'left',
    fontSize: 14,
    cursor: 'pointer !important',
    letterSpacing: '0px',
    opacity: '1',
  },
  subMenuItem: {
    height: '16px',
    textAlign: 'left',
    marginLeft: '9px',
    fontSize: 12,
    cursor: 'pointer !important',
    letterSpacing: '0px',
    opacity: '1',
  },
  tab: {
    '& .MuiButton-label': {
      justifyContent: 'start',
    },
    width: '100%',
    wordBreak: 'keep-all',
    textAlign: 'start',
    margin: '7px 10px',
  },
  selected: {
    backgroundColor: theme.palette.system.violet,
    height: '26px',
    width: '4px',
    '@media (min-width: 768px) and (max-width: 1024px)': {
      height: '22px',
    },
  },
  unselected: {
    height: '26px',
    width: '4px',
  },
  textColorSelected: {
    color: theme.palette.system.violet,
    fontWeight: 500,
  },
  textFontSelected: {
    fontWeight: 500,
  },
  textColorUnselected: {
    color: theme.palette.components.typography.color,
    fontWeight: 500,
    '&:hover': {
      color: theme.palette.system.violet,
    },
  },
  textColorUnselectedGrey: {
    color: theme.palette.system.darkGrey,
  },
}));

export const OrderNavigationMenu = ({ navRefs, activeStep, onItemClick }) => {
  const { classes, cx } = useStyles();
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const { t } = useTranslation();
  const page1 = useSelector(selectPage1);
  const page2 = useSelector(selectPage2);
  const page3 = useSelector(selectPage3);
  const disableBtnsidePanel = useSelector(selectDisableNextBtnSidePanel);
  const orderDetails = useSelector(selectNewOrderCreation);

  useEffect(() => {
    setActiveIndex(navRefs.findIndex(item => item.categoryIndex === activeStep));
  }, [navRefs, activeIndex, activeStep]);

  const handleClick = index => {
    setActiveIndex(index);
    onItemClick(navRefs[index].categoryIndex);
  };

  const checkFreeTextItems = (item: any, itemLabel: string, key: string, itemId: number): boolean => {
    return item?.label === itemLabel && item?.id === itemId && (orderDetails?.[key]?.length ?? 0) > 0;
  };
  const handelTabSelection = (index, activeTabIndex, item) => {
    if (item?.categoryIndex === navRefs[activeTabIndex].categoryIndex && item?.mainMenuItem) {
      return true;
    } else {
      switch (item?.categoryIndex) {
        case 1:
          if (!item?.mainMenuItem) {
            if (item.label === `${t('order.newOrderCreation.tabs.orderTypeDetails')}` && page1?.region !== '') {
              return true;
            } else if (
              item.label === `${t('order.newOrderCreation.tabs.experience')}` &&
              page1?.skillSetGroup?.groups?.length > 0 &&
              page1?.skillSetGroup?.groups[0]?.skillsets?.length > 0 &&
              page1?.skillSetGroup?.groups[0]?.skillsets[0]?.skillSet?.discipline !== null &&
              page1?.skillSetGroup?.groups[0]?.skillsets[0]?.skillSet?.specialty !== null
            ) {
              return true;
            }
          }
          break;
        case 2:
          if (!item?.mainMenuItem) {
            if (
              item.label === `${t('order.newOrderCreation.tabs.clinicianToPatientRatio')}` &&
              (page2?.orderClinicianToPatientRatio?.notifyCandidate === true ||
                page2?.orderClinicianToPatientRatio?.numberOfClinicians > 0 ||
                page2?.orderClinicianToPatientRatio?.numberOfPatients > 0 ||
                page2?.orderClinicianToPatientRatio?.numberOfVisits > 0 ||
                page2?.orderClinicianToPatientRatio?.patientsSeen > 0 ||
                page2?.orderClinicianToPatientRatio?.productivityPercentage > 0)
            ) {
              return true;
            } else if (
              (item.label === `${t('order.newOrderCreation.tabs.orderPriorities')}` ||
                item.label === `${t('order.newOrderCreation.tabs.virtualInterviewRating')}`) &&
              page2?.orderPriorities?.length > 0
            ) {
              return true;
            } else if (item.label === `${t('order.newOrderCreation.tabs.bestBets')}` && page2?.bestBets?.length > 0) {
              return true;
            } else if (
              item.label === `${t('order.newOrderCreation.tabs.workHistory')}` &&
              (page2?.orderWorkHistory?.entireWorkHistory === true ||
                page2?.orderWorkHistory?.workHistoryGapInDays > 0 ||
                page2?.orderWorkHistory?.workHistoryGapInYears > 0)
            ) {
              return true;
            } else if (
              item.label === `${t('order.newOrderCreation.tabs.references')}` &&
              (page2?.reference?.isRequired === true ||
                page2?.reference?.referenceFromSupervisor === true ||
                page2?.reference?.numberOfReferenceRequired > 0 ||
                page2?.reference?.numberOfReferenceToRFO > 0)
            ) {
              return true;
            } else if (
              item.label === `${t('order.newOrderCreation.tabs.strikrDetails')}` &&
              (page2?.orderStrike?.strikeNoticeDate !== null ||
                page2?.reference?.referenceFromSupervisor === true ||
                page2?.orderStrike?.okToTravel === true)
            ) {
              return true;
            }
          }
          break;
        case 3:
          if (!item?.mainMenuItem) {
            if (
              item.label === `${t('order.newOrderCreation.tabs.schedule')}` &&
              (page3?.shifts?.length > 0 ||
                page3?.additionalInformation?.allowBlocks?.isSelected === true ||
                page3?.additionalInformation?.float?.isSelected === true ||
                page3?.additionalInformation?.holidays?.isSelected === true ||
                page3?.additionalInformation?.noTimeOffAllowed?.isSelected === true ||
                page3?.additionalInformation?.onCall?.isSelected === true ||
                page3?.additionalInformation?.weekends?.isSelected === true ||
                page3?.additionalInformation?.scrubColors?.length > 0)
            ) {
              return true;
            }
            if (
              item.label === `${t('order.newOrderCreation.tabs.placementTerms')}` &&
              (page3?.ASAP === true ||
                page3?.flexibleDates === true ||
                page3?.placementLength > 0 ||
                page3?.startDate !== null ||
                page3?.endDate !== null)
            ) {
              return true;
            } else if (
              item.label === `${t('order.newOrderCreation.tabs.technologyInformation')}` &&
              (page3?.VMSOrderNumber?.length > 0 || page3?.webLink?.length > 0 || page3?.VMSBillRate)
            ) {
              return true;
            }
            if (
              checkFreeTextItems(
                item,
                `${t('order.newOrderCreation.tabs.additionRequirements')}`,
                'additionalRequiredQualifications',
                16,
              )
            ) {
              return true;
            }
            if (
              checkFreeTextItems(
                item,
                `${t('order.newOrderCreation.tabs.preferredQualification')}`,
                'preferredQualifications',
                17,
              )
            ) {
              return true;
            }
            if (checkFreeTextItems(item, `${t('order.newOrderCreation.tabs.internalNotes')}`, 'internalNotes', 18)) {
              return true;
            }
            if (
              checkFreeTextItems(
                item,
                `${t('order.newOrderCreation.tabs.externalJobDescription')}`,
                'jobDescription',
                19,
              )
            ) {
              return true;
            }
          }
          break;
      }
      return false;
    }
  };

  return (
    <React.Fragment>
      <div className={classes.leftTab}>
        <LayoutGrid className={classes.menu}>
          {navRefs &&
            navRefs.map((item, index) => {
              return (
                <Box key={index} className={classes.tab} sx={{ pointerEvents: !disableBtnsidePanel ? 'all' : 'none' }}>
                  <LayoutGrid container>
                    <LayoutGrid item xs={12} md={1}>
                      <LayoutGridItem item sx={{ padding: '0px  !important' }}>
                        <Box
                          className={
                            item.categoryIndex === navRefs[activeIndex]?.categoryIndex && item?.mainMenuItem
                              ? classes.selected
                              : classes.unselected
                          }
                        />
                      </LayoutGridItem>
                    </LayoutGrid>
                    <LayoutGrid item xs={12} md={11}>
                      <LayoutGridItem item sx={{ padding: '0px  !important', marginBottom: item?.multiLine && '25px' }}>
                        <Typography
                          variant="body2"
                          classes={{
                            root: cx({
                              [classes.mainMenuItem]: item?.mainMenuItem,
                              [classes.subMenuItem]: !item?.mainMenuItem,
                              [classes.textFontSelected]:
                                item.categoryIndex === navRefs[activeIndex].categoryIndex && item?.mainMenuItem,
                              [classes.textColorSelected]:
                                handelTabSelection(index, activeIndex, item) && activeStep === item.categoryIndex,
                              [classes.textColorUnselected]:
                                handelTabSelection(index, activeIndex, item) && activeStep !== item.categoryIndex,
                              [classes.textColorUnselectedGrey]:
                                !handelTabSelection(index, activeIndex, item) && !item?.mainMenuItem,
                            }),
                          }}
                          onClick={() => {
                            handleClick(index);
                          }}
                        >
                          {item.label}
                        </Typography>
                      </LayoutGridItem>
                    </LayoutGrid>
                  </LayoutGrid>
                </Box>
              );
            })}
        </LayoutGrid>
      </div>
    </React.Fragment>
  );
};
